// Zod schemas for Borg API response validation.
// These schemas validate the shape of data coming from the Borg backend at runtime,
// catching unexpected changes before they cause UI crashes.

import { z } from "zod";

const timeSchema = z.coerce.date().transform((date) => date.getTime());

// -- Status enum --

export const borgStatusEnum = z.enum(["in_progress", "completed", "failed", "timed_out", "cancelled", "canceling"]);

// -- Known finite value enums --

export const borgSeverityEnum = z.enum(["info", "low", "medium", "high", "critical"]);
export const borgToolStatusEnum = z.enum(["pending", "complete", "failed"]);
export const borgActionTypeEnum = z.enum(["view_component", "view_logs", "view_metrics", "view_traces"]);
export const borgAgentTypeEnum = z.enum(["adaptive", "chat", "investigation", "promql_assist", "docs_assist"]);
export const borgLinkTypeEnum = z.enum(["component", "documentation", "external"]);
export const borgTriggerSourceEnum = z.enum(["api", "cli", "slack"]);

// -- Content parts --

const borgTextPartSchema = z.object({
	type: z.literal("text"),
	part_id: z.string().optional(),
	content: z.string().optional(),
	severity: borgSeverityEnum.optional(),
});

const borgThinkingPartSchema = z.object({
	type: z.literal("thinking"),
	part_id: z.string().optional(),
	content: z.string().optional(),
});

const borgToolCallPartSchema = z.object({
	type: z.literal("tool_call"),
	part_id: z.string().optional(),
	tool_name: z.string().optional(),
	tool_use_id: z.string().optional(),
	tool_args: z.record(z.unknown()).optional(),
	tool_status: borgToolStatusEnum.optional(),
	duration_ms: z.number().optional(),
});

const borgProgressPartSchema = z.object({
	type: z.literal("progress"),
	part_id: z.string().optional(),
	content: z.string().optional(),
});

const borgSuggestionPartSchema = z.object({
	type: z.literal("suggestion"),
	part_id: z.string().optional(),
	content: z.string().optional(),
});

const borgSourceLinkPartSchema = z.object({
	type: z.literal("source_link"),
	part_id: z.string().optional(),
	label: z.string().optional(),
	urn: z.string().optional(),
	url: z.string().optional(),
	link_type: borgLinkTypeEnum.optional(),
});

const borgActionPartSchema = z.object({
	type: z.literal("action"),
	part_id: z.string().optional(),
	label: z.string().optional(),
	urn: z.string().optional(),
	url: z.string().optional(),
	action_type: borgActionTypeEnum.optional(),
	query: z.string().optional(),
});

const borgErrorPartSchema = z.object({
	type: z.literal("error"),
	part_id: z.string().optional(),
	content: z.string().optional(),
});

export const borgContentPartSchema = z.discriminatedUnion("type", [
	borgTextPartSchema,
	borgThinkingPartSchema,
	borgToolCallPartSchema,
	borgProgressPartSchema,
	borgSuggestionPartSchema,
	borgSourceLinkPartSchema,
	borgActionPartSchema,
	borgErrorPartSchema,
]);

// -- Context pins (discriminated union by type) --

const borgComponentPinSchema = z.object({
	type: z.literal("component"),
	name: z.string().optional(),
	urn: z.string().optional(),
	component_type: z.string().optional(),
	health_state: z.string().optional(),
	monitor_name: z.string().optional(),
});

const borgTopologyTimePinSchema = z.object({
	type: z.literal("topology_time"),
	point_ms: z.number().optional(),
	is_live: z.boolean().optional(),
});

const borgTelemetryIntervalPinSchema = z.object({
	type: z.literal("telemetry_interval"),
	start_ms: z.number().optional(),
	end_ms: z.number().optional(),
	range_label: z.string().optional(),
	markers_ms: z.array(z.number()).optional(),
});

const borgMetricTimeRangePinSchema = z.object({
	type: z.literal("metric_time_range"),
	start_ms: z.number().optional(),
	end_ms: z.number().optional(),
	range_label: z.string().optional(),
});

const borgTimeRangePinSchema = z.object({
	type: z.literal("time_range"),
	start_ms: z.number().optional(),
	end_ms: z.number().optional(),
	point_ms: z.number().optional(),
});

const borgFilterPinSchema = z.object({
	type: z.literal("filter"),
	name: z.string().optional(),
	value: z.string().optional(),
});

const borgQueryPinSchema = z.object({
	type: z.literal("query"),
	query: z.string().optional(),
});

export const borgContextPinSchema = z.discriminatedUnion("type", [
	borgComponentPinSchema,
	borgTopologyTimePinSchema,
	borgTelemetryIntervalPinSchema,
	borgMetricTimeRangePinSchema,
	borgTimeRangePinSchema,
	borgFilterPinSchema,
	borgQueryPinSchema,
]);

// -- Messages --

export const borgMessageSchema = z.object({
	id: z.string(),
	role: z.enum(["user", "assistant", "system"]),
	parts: z.array(borgContentPartSchema),
	agent_type: borgAgentTypeEnum.nullish(),
	selection_mode: z.enum(["auto", ""]).nullish(),
	context_pins: z.array(borgContextPinSchema).optional(),
	created_at: timeSchema,
});

// -- Shared tool call --

export const borgStatusToolCallSchema = z.object({
	tool_name: z.string(),
	tool_args: z.string(),
	status: borgToolStatusEnum,
	duration_ms: z.number().optional(),
});

// -- Conversation status --

export const borgAgentRunBriefSchema = z.object({
	id: z.string(),
	agent_type: borgAgentTypeEnum,
	status: borgStatusEnum,
	turns: z.number(),
	tool_calls: z.number(),
	input: z.string().optional(),
	output: z.string().optional(),
});

export const borgConversationStatusSchema = z.object({
	id: z.string(),
	status: borgStatusEnum,
	agent: borgAgentTypeEnum,
	active_agent: borgAgentTypeEnum.optional(),
	total_turns: z.number(),
	total_tool_calls: z.number(),
	message_count: z.number().optional(),
	progress_updates: z.array(z.string()).optional(),
	recent_tool_calls: z.array(borgStatusToolCallSchema).optional(),
	agent_runs: z.array(borgAgentRunBriefSchema).optional(),
	response: z.string().optional(),
	started_at: timeSchema,
	elapsed_seconds: z.number(),
	completed_at: timeSchema.optional(),
	messages: z.array(borgMessageSchema).optional(),
	current_message: borgMessageSchema.optional(),
});

export const borgConversationTranscriptSchema = z.object({
	id: z.string(),
	message_count: z.number(),
	messages: z.array(borgMessageSchema),
});

// -- Conversation list --

export const borgConversationListItemSchema = z.object({
	id: z.string(),
	number: z.number(),
	status: borgStatusEnum,
	trigger_source: borgTriggerSourceEnum,
	initial_message: z.string(),
	started_at: timeSchema,
	total_turns: z.number(),
	total_tool_calls: z.number(),
	display_name: z.string().optional(),
	agent: borgAgentTypeEnum,
});

export const borgConversationListResponseSchema = z.object({
	conversations: z.array(borgConversationListItemSchema),
});

// -- Agent info --

export const borgAgentStatusEnum = z.enum(["active", "unavailable"]);

export const borgAgentInfoSchema = z.object({
	type: borgAgentTypeEnum,
	display_name: z.string(),
	description: z.string(),
	status: borgAgentStatusEnum,
	reason: z.string().optional(),
});

export const borgAgentsResponseSchema = z.array(borgAgentInfoSchema);

// -- Generic mutation responses --

export const borgStatusResponseSchema = z.object({
	status: borgStatusEnum,
});

export const borgIdStatusResponseSchema = z.object({
	id: z.string(),
	status: borgStatusEnum,
});
